import { useState } from 'react';

import { Button } from '@/components/ui/button';
import type { ProposedEntry } from './PrenatalEntry';
import { usePermissions } from '@/features/permissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
export function CareStatusEntry({
  onSave,
  disabled,
  subjectId,
}: {
  subjectId: string;
  onSave: (entries: ProposedEntry[], time: string) => Promise<void>;
  disabled: boolean;
}) {
  const { permissions } = usePermissions();
  const [status, setStatus] = useState('PRENATAL');
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  if (!permissions.includes('create_clinical_event') && !permissions.includes('create_supervised_clinical_event'))
    return null;
  const save = async () => {
    setBusy(true);
    setMessage('');
    try {
      await onSave(
        [
          {
            subject_id: subjectId,
            event_type: 'PRENATAL.CARE_STATUS',
            payload: { status, effective_date: date, reason: reason.trim() },
          },
        ],
        new Date().toISOString(),
      );
      setMessage('Care status recorded. It appears in the roster once synced and reviewed if required.');
      setReviewed(false);
    } catch {
      setMessage('Care status was not saved; your entry is retained.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Record care status</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p>
          This records the clinician’s assessment of the care phase. It does not create a birth record or close the
          pregnancy. For postpartum care, the effective date is the documented birth date used for postpartum days.
        </p>
        <fieldset disabled={disabled || busy} className="flex flex-wrap gap-3">
          <label>
            Status{' '}
            <select
              value={status}
              className="min-h-11 border"
              onChange={(e) => {
                setStatus(e.target.value);
                setReviewed(false);
              }}>
              {['PRENATAL', 'IN_LABOR', 'POSTPARTUM', 'TRANSFERRED', 'CLOSED', 'PREGNANCY_LOSS'].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
          <label>
            Effective date{' '}
            <input
              type="date"
              value={date}
              className="min-h-11 border"
              onChange={(e) => {
                setDate(e.target.value);
                setReviewed(false);
              }}
            />
          </label>
          <label className="w-full">
            Reason / source{' '}
            <textarea
              rows={3}
              value={reason}
              maxLength={2000}
              className="w-full border"
              onChange={(e) => {
                setReason(e.target.value);
                setReviewed(false);
              }}
            />
          </label>
          <label>
            <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} /> I reviewed the
            care status and effective date.
          </label>
        </fieldset>
        <Button disabled={disabled || busy || !reviewed || !date || !reason.trim()} onClick={() => void save()}>
          Record status
        </Button>
        {message && <p role="status">{message}</p>}
      </CardContent>
    </Card>
  );
}
